define([
  "backbone",
  "i18n/i18n",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, i18next, Messenger, Properties, SortableListModel) {
  "use strict";

  const ApiKeyModel = {};
  const tenantApiKeys = {};
  const ROLE_ENUM = Object.freeze({
    TASK_CREATION: 100,
    TASK_CREATION_WITH_ATTRIBUTES: 110,
    REPORTING: 200,
    AUTHORING_TOOLS: 300,
    AD_HOC_REPORTING: 400,
    AD_HOC_REPORTING_ADMIN: 500,
    TOPIC_IMPORT_API: 600,
    TOPIC_SEARCH_API: 700,
    ENGINE: 1000,
    TOPIC_EXPORT_API: 1300,
    FORD_API: 1400,
    TOPIC_PUBLISH_API: 1500,
    READ_TOPIC_RESPONSE: 1600,
  });

  function parseRole(value) {
    switch (typeof value) {
      case "number":
        for (const role of Object.keys(ROLE_ENUM)) {
          if (value === ROLE_ENUM[role]) {
            return {
              roleId: ROLE_ENUM[role],
              roleName: role,
            };
          }
        }
        throw new Error(`Unexpected role ${value}`);
      case "string":
        for (const role of Object.keys(ROLE_ENUM)) {
          if (value.toUpperCase() === role) {
            return {
              roleId: ROLE_ENUM[role],
              roleName: role,
            };
          }
        }
        throw new Error(`Unexpected role ${value}`);
      default:
        throw new Error(`Unexpected role ${value}`);
    }
  }

  ApiKeyModel.role = Backbone.Model.extend(
    {
      idAttribute: "roleId",
      parse: function (value) {
        return parseRole(value);
      },
      toJSON: function () {
        return this.get("roleName");
      },
    },
    {
      ROLE_ENUM: ROLE_ENUM,
    },
  );

  ApiKeyModel.tenantAllowedRoles = Backbone.AtomicCollection.extend({
    model: ApiKeyModel.role,
    addRole: function (role) {
      this.add(new ApiKeyModel.role(role, { parse: true }));
    },
    url: function () {
      let tenantId = this.tenantId || Messenger.reqres.request("active:tenantId");
      return `${Properties.adminRootURL}/apiKeys/tenants/${tenantId}/allowedRoles`;
    },
  });

  ApiKeyModel.apiKeyLanguage = Backbone.Model.extend({});

  ApiKeyModel.apiKeyLanguages = Backbone.Collection.extend({
    model: ApiKeyModel.apiKeyLanguage,
    addLanguage: function (kbId, languageId) {
      const apiKeyLanguage = new ApiKeyModel.apiKeyLanguage({
        kbId,
        languageId,
      });
      this.add(apiKeyLanguage);
      return apiKeyLanguage;
    },
  });

  ApiKeyModel.apiKeyRole = Backbone.Model.extend({
    parse: function (response) {
      const parsedRole = parseRole(response.role);
      if (parsedRole && parsedRole.roleName) {
        response.roleId = parsedRole.roleId;
        response.roleName = parsedRole.roleName;
      }
      return response;
    },
    toJSON: function (options) {
      const json = Object.assign({}, this.attributes);
      if (options && (options.parse || options.saving)) {
        json.role = json.roleName;
        delete json.roleId;
      }
      return json;
    },
  });

  ApiKeyModel.apiKeyRoles = Backbone.Collection.extend({
    model: ApiKeyModel.apiKeyRole,
    addRole: function (kbId, roleId) {
      const parsedRole = parseRole(roleId);
      if (parsedRole) {
        const role = new ApiKeyModel.apiKeyRole({
          kbId: kbId,
          roleId: parsedRole.roleId,
          roleName: parsedRole.roleName,
        });
        this.add(role);
        return role;
      }
      throw new Error("Invalid role " + roleId);
    },
  });

  ApiKeyModel.apiKeyTouchpoint = Backbone.Model.extend({});

  ApiKeyModel.apiKeyTouchpoints = Backbone.Collection.extend({
    model: ApiKeyModel.apiKeyTouchpoint,
    addTouchpoint: function (kbId, touchpointId) {
      const apiKeyTouchpoint = new ApiKeyModel.apiKeyTouchpoint({
        kbId,
        touchpointId,
      });
      this.add(apiKeyTouchpoint);
      return apiKeyTouchpoint;
    },
  });

  ApiKeyModel.apiKey = Backbone.Model.extend({
    defaults: {
      name: "",
      expiresAt: null,
      isPrivate: false,
      revoked: false,
      value: "",
    },
    parse: function (response) {
      if (response.languages) {
        this.languages().reset(response.languages, { parse: true });
        delete response.languages;
      }
      if (response.roles) {
        this.roles().reset(response.roles, { parse: true });
        delete response.roles;
      }
      if (response.touchpoints) {
        this.touchpoints().reset(response.languages, { parse: true });
        delete response.touchpoints;
      }
      return response;
    },
    renameApiKey: function (name) {
      if (!this.id) {
        throw new Error("Cannot rename a new API Key");
      }
      const oldName = this.get("name");
      if (oldName == name) {
        return;
      }
      this.set("name", name);
      const url = this.url() + "/rename";
      return this.saveModelPromise({
        url: url,
      }).catch(() => this.set("name", oldName));
    },
    revokeApiKey: function () {
      if (!this.id) {
        throw new Error("Cannot revoke a new API Key");
      }
      if (this.get("revoked")) {
        return;
      }
      this.set("revoked", true);
      const url = this.url() + "/revoke";
      return this.saveModelPromise({
        url: url,
      }).catch(() => this.set("revoked", false));
    },
    languages: function () {
      if (!this.languagesCol) {
        this.languagesCol = new ApiKeyModel.apiKeyLanguages();
        this.languagesCol.apiKey = this;
      }
      return this.languagesCol;
    },
    roles: function () {
      if (!this.rolesCol) {
        this.rolesCol = new ApiKeyModel.apiKeyRoles();
        this.rolesCol.apiKey = this;
      }
      return this.rolesCol;
    },
    touchpoints: function () {
      if (!this.touchpointsCol) {
        this.touchpointsCol = new ApiKeyModel.apiKeyTouchpoints();
        this.touchpointsCol.apiKey = this;
      }
      return this.touchpointsCol;
    },
    toJSON: function (options) {
      const json = Object.assign({}, this.attributes);
      if (options && (options.parse || options.saving)) {
        if (this.languagesCol) {
          json.languages = this.languagesCol.toJSON(options);
        }
        json.roles = this.roles().toJSON(options);
        if (this.touchpointsCol) {
          json.touchpoints = this.touchpointsCol.toJSON(options);
        }
      }
      return json;
    },
  });

  ApiKeyModel.apiKeys = Backbone.Collection.extend({
    model: ApiKeyModel.apiKey,
    addApiKey: function () {
      const apiKey = new ApiKeyModel.apiKey();
      apiKey.tenantId = this.tenantId;
      this.add(apiKey);
      return apiKey;
    },
    url: function () {
      const tenantId = this.tenantId || Messenger.reqres.request("active:tenantId");
      return `${Properties.adminRootURL}/apiKeys/tenants/${tenantId}`;
    },
  });

  ApiKeyModel.privateApiKey = ApiKeyModel.apiKey.extend({
    defaults: {
      ...ApiKeyModel.apiKey.defaults,
      isPrivate: true,
    },
    url: function () {
      const tenantId = this.tenantId || Messenger.reqres.request("active:tenantId");
      return `${Properties.adminRootURL}/apiKeys/tenants/${tenantId}/privateReadOnly`;
    },
  });

  Messenger.reqres.setHandler(
    "apiKey:createPrivate",
    function ({ kb, languageId, roleId, touchpointId }) {
      if (!languageId) {
        throw new Error("Language is required to create a private API Key");
      }
      if (!touchpointId) {
        throw new Error("Touchpoint is required to create a private API Key");
      }
      if (!kb) {
        kb = Messenger.reqres.request("active:kb:model");
      }
      if (!roleId) {
        roleId = ROLE_ENUM.READ_TOPIC_RESPONSE;
      }
      const kbId = kb.id;
      const apiKey = new ApiKeyModel.privateApiKey({
        isPrivate: true,
      });
      apiKey.tenantId = kb.get("tenantId");
      apiKey.languages().addLanguage(kbId, languageId);
      apiKey.roles().addRole(kbId, roleId);
      apiKey.touchpoints().addTouchpoint(kbId, touchpointId);
      return apiKey;
    },
  );

  Messenger.reqres.setHandler("apiKey:tenant:allowedRoles", function (tenantId) {
    const tenantAllowedRoles = new ApiKeyModel.tenantAllowedRoles();
    tenantAllowedRoles.tenantId = tenantId;
    tenantAllowedRoles.fetch();
    return tenantAllowedRoles;
  });

  Messenger.reqres.setHandler("tenant:apiKeys", function ({ tenantId } = { tenantId: 0 }) {
    if (!tenantId) {
      return null;
    }
    let apiKeys = tenantApiKeys[tenantId];
    if (!apiKeys) {
      apiKeys = new ApiKeyModel.apiKeys();
      apiKeys.tenantId = tenantId;
      apiKeys.fetch();
      tenantApiKeys[tenantId] = apiKeys;
    }
    return apiKeys;
  });

  Messenger.reqres.setHandler("apiKey:list:headers", function () {
    return new SortableListModel.headerCollection([
      {
        headerName: i18next.t("apiKey.name"),
        modelAttribute: "name",
        filterable: true,
        longTextField: true,
        selectable: true,
        sortable: true,
        width: "40%",
      },
      {
        headerName: i18next.t("apiKey.expiresAt"),
        modelAttribute: "expiresAt",
        dateField: true,
        dateOnly: true,
        filterable: true,
        hideable: true,
        sortable: true,
        width: "20%",
      },
      {
        headerName: i18next.t("apiKey.revoked"),
        modelAttribute: "revoked",
        booleanField: true,
        booleanFieldCheck: true,
        filterable: true,
        hideable: true,
        width: "15%",
      },
      {
        headerName: "",
        modelAttribute: "name",
        width: "25%",
        customHtml: `<button type="button"
           class="btn btn-danger btn-mini deleteApiKey"
           data-i18n="[title]apiKey.deleteApiKey"
           data-toggle="tooltip">
           <i class="icon-remove"></i>
         </button>
         <button type="button"
           class="btn btn-mini revokeApiKey"
           data-i18n="[title]apiKey.revokeApiKey"
           data-toggle="tooltip">
           <i class="icon-stop"></i>
         </button>
         <button type="button"
           class="btn btn-mini copyApiKey"
           data-i18n="[title]apiKey.copyApiKey"
           data-toggle="tooltip">
           <i class="icon-copy"></i>
         </button>`,
      },
    ]);
  });

  return ApiKeyModel;
});
